import { useMemo } from "react";
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
} from "@mui/x-data-grid-pro";
import {
  IQuoteResponseItem,
  QuoteStatus,
  currencyFormatter,
  langauge,
  toDecimal,
  viewableQuoteStatuses,
  getAccountingSystemDeepLink,
} from "shared";
import {
  DocumentArrowUpIcon,
  DocumentCheckIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { Tooltip } from "@mui/material";

import { useContactQuotes } from "../../../../hooks/useContactQuotes";
import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";
import { useAccountingSystemTerm } from "../../../../hooks/useAccountingSystemTerm";
import { DatePopover } from "../../../../../common/Components/DatePopover";
import { WrappedLink } from "../../../WrappedLink";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { Badge } from "../../../../../common/Atoms/Badge";
import { env } from "../../../../../common/lib/env";
import { commonConfig } from "../../../../lib/datagrid";
import { DatagridCard } from "../../../Common/Datagrid/DatagridCard";

const statusColors: { [key in QuoteStatus]: `gray` | `blue` | `green` | `red` | `purple` } = {
  [QuoteStatus.DRAFT]: `gray`,
  [QuoteStatus.SENT]: `blue`,
  [QuoteStatus.ACCEPTED]: `green`,
  [QuoteStatus.DECLINED]: `red`,
  [QuoteStatus.CONVERTED]: `purple`,
  [QuoteStatus.DELETED]: `red`,
  [QuoteStatus.UNKNOWN]: `gray`,
};

function homeTotalCents(items: IQuoteResponseItem[]) {
  return items.reduce((acc, q) => acc + Math.round(q.amountTotalCents * (q.exchangeRate || 1)), 0);
}

export function ContactQuotes() {
  const { quotes, isLoading } = useContactQuotes();
  const currentOrg = useGetSelectedOrganisation();
  const quotesTerm = useAccountingSystemTerm(`Quotes`);
  const quoteTermSingular = useAccountingSystemTerm(`Quote`);
  const formatter = currencyFormatter(currentOrg?.baseCurrency);

  const rows = useMemo(() => {
    return Object.values(quotes).flat() as IQuoteResponseItem[];
  }, [quotes]);

  const sentQuotes = quotes[QuoteStatus.SENT] || [];
  const acceptedQuotes = quotes[QuoteStatus.ACCEPTED] || [];

  // Open quotes/estimates = sent & accepted, not yet converted to an invoice
  const openQuotes = useMemo(() => [
    ...sentQuotes,
    ...acceptedQuotes,
  ], [quotes]);

  const openCurrencyBreakdown = useMemo(() => {
    const totals: { [currency: string]: number } = {};

    openQuotes.forEach(q => {
      totals[q.currency] = (totals[q.currency] || 0) + q.amountTotalCents;
    });

    return Object.entries(totals).map(([code, cents]) => ({
      code,
      total: currencyFormatter(code, currentOrg?.baseCurrency)(toDecimal(cents)),
    }));
  }, [openQuotes, currentOrg]);

  const showOpenBreakdown = openCurrencyBreakdown.some(item => item.code !== currentOrg?.baseCurrency);

  function onPortalView(hash: string) {
    window.open(`${env.portalWebUrl}/q/${hash}`, `_blank`, `noopener noreferrer`);
  }

  const columns: GridColDef<IQuoteResponseItem>[] = useMemo(() => {
    return [
      {
        field: `issueDateString`,
        headerName: `Issue Date`,
        flex: 1,
        sortComparator: (v1, v2) => {
          return new Date(v1).getTime() - new Date(v2).getTime();
        },
        renderCell: ({ row }) => {
          return <DatePopover
            date={ row.issueDateString }
            labelFormat={ `dd LLL yyyy` }
            organisationTimezone={ currentOrg?.validatedTimezone }
          />;
        },
      },
      {
        field: `number`,
        headerName: `Number`,
        flex: 1,
        renderCell: ({ row }) => {
          return (
            <WrappedLink
              to={ `/quotes/${ row.id }` }
            >
              <Paragraph
                variant={ `link` }
                as={ `span` }
              >
                { row.number }
              </Paragraph>
            </WrappedLink>
          );
        },
      },
      {
        field: `expiryDateString`,
        headerName: `Expiry Date`,
        flex: 1,
        sortComparator: (v1, v2) => {
          // No expiry for both, dont sort
          if (!v1 && !v2) return 0;

          if (!v1) return 1;

          if (!v2) return -1;

          return new Date(v1).getTime() - new Date(v2).getTime();
        },
        renderCell: ({ row }) => {
          if (!row.expiryDateString) return `-`;

          return <DatePopover
            date={ row.expiryDateString }
            labelFormat={ `dd LLL yyyy` }
            organisationTimezone={ currentOrg?.validatedTimezone }
          />;
        },
      },
      {
        field: `status`,
        headerName: `Status`,
        flex: 1,
        valueGetter: (value, row) => langauge.quotes.status[row.status],
        renderCell: ({ row }) => {
          return (
            <Badge
              message={ langauge.quotes.status[row.status] }
              color={ statusColors[row.status] }
            />
          );
        },
      },
      {
        field: `totalAmountFormatted`,
        headerName: `Value`,
        flex: 1,
        valueGetter: (value, row)  => row.amountTotalCents,
        valueFormatter: (value, row) => row.totalAmountFormatted,
      },
      {
        field: `actions`,
        type: `actions`,
        getActions: ({ row }) => {
          const items = [
            <GridActionsCellItem
              key={ `${row.id}-portal-link` }
              onClick={ () => onPortalView(row.hash) }
              label={ `View Customer Portal` }
              disabled={ !viewableQuoteStatuses.includes(row.status) }
              showInMenu
            />,
          ];

          const externalLink = getAccountingSystemDeepLink(currentOrg, `quote`, row.externalId);
          if (externalLink) {
            items.push(
              <GridActionsCellItem
                key={ `${row.id}-external-link` }
                onClick={ () => window.open(externalLink, `_blank`, `noopener noreferrer`) }
                label={ `View in ${currentOrg?.accountingSystemName}` }
                showInMenu
              />,
            );
          }

          return items;
        },
      },
    ];
  }, [currentOrg]);

  return (
    <div className={ `mt-4` }>
      {
        openQuotes.length ? (
          <div className={ `mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3` }>
            <Tooltip title={ `Sent & accepted ${quotesTerm.toLowerCase()}, converted to ${currentOrg?.baseCurrency}` }>
              <div className={ `group relative overflow-hidden rounded-lg bg-gray-50 ring-1 ring-gray-200 p-3 hover:bg-white hover:shadow-xs transition` }>
                <div className={ `flex items-start gap-3` }>
                  <div className={ `rounded-md bg-teal-50 p-2` }>
                    <DocumentTextIcon className={ `h-5 w-5 text-teal-600` } />
                  </div>
                  <div className={ `min-w-0` }>
                    <div className={ `text-[11px] uppercase tracking-wide text-gray-500` }>{ `Open ${quotesTerm}` }</div>
                    <div className={ `text-lg font-semibold text-gray-900 truncate` }>{ formatter(toDecimal(homeTotalCents(openQuotes))) }</div>
                    {
                      showOpenBreakdown ? (
                        <div className={ `mt-1 ml-2 pl-3 border-l border-gray-200 space-y-1` }>
                          {
                            openCurrencyBreakdown.map(item => (
                              <div
                                key={ `open-${item.code}` }
                                className={ `text-[11px] text-gray-600 leading-tight` }
                              >
                                { item.total }
                              </div>
                            ))
                          }
                        </div>
                      ) : null
                    }
                  </div>
                </div>
              </div>
            </Tooltip>

            <Tooltip title={ `Awaiting customer response, converted to ${currentOrg?.baseCurrency}` }>
              <div className={ `group relative overflow-hidden rounded-lg bg-gray-50 ring-1 ring-gray-200 p-3 hover:bg-white hover:shadow-xs transition` }>
                <div className={ `flex items-start gap-3` }>
                  <div className={ `rounded-md bg-blue-50 p-2` }>
                    <DocumentArrowUpIcon className={ `h-5 w-5 text-blue-600` } />
                  </div>
                  <div className={ `min-w-0` }>
                    <div className={ `text-[11px] uppercase tracking-wide text-gray-500` }>{ langauge.quotes.status[QuoteStatus.SENT] }</div>
                    <div className={ `text-lg font-semibold text-gray-900 truncate` }>{ formatter(toDecimal(homeTotalCents(sentQuotes))) }</div>
                    <div className={ `text-[11px] text-gray-600 leading-tight truncate` }>
                      { `${sentQuotes.length} ${sentQuotes.length === 1 ? quoteTermSingular.toLowerCase() : quotesTerm.toLowerCase()}` }
                    </div>
                  </div>
                </div>
              </div>
            </Tooltip>

            <Tooltip title={ `Accepted but not yet invoiced, converted to ${currentOrg?.baseCurrency}` }>
              <div className={ `group relative overflow-hidden rounded-lg bg-gray-50 ring-1 ring-gray-200 p-3 hover:bg-white hover:shadow-xs transition` }>
                <div className={ `flex items-start gap-3` }>
                  <div className={ `rounded-md bg-green-50 p-2` }>
                    <DocumentCheckIcon className={ `h-5 w-5 text-green-600` } />
                  </div>
                  <div className={ `min-w-0` }>
                    <div className={ `text-[11px] uppercase tracking-wide text-gray-500` }>{ langauge.quotes.status[QuoteStatus.ACCEPTED] }</div>
                    <div className={ `text-lg font-semibold text-gray-900 truncate` }>{ formatter(toDecimal(homeTotalCents(acceptedQuotes))) }</div>
                    <div className={ `text-[11px] text-gray-600 leading-tight truncate` }>
                      { `${acceptedQuotes.length} ${acceptedQuotes.length === 1 ? quoteTermSingular.toLowerCase() : quotesTerm.toLowerCase()}` }
                    </div>
                  </div>
                </div>
              </div>
            </Tooltip>
          </div>
        ) : null
      }

      <div className={ `h-125` }>
        <DatagridCard className={ `h-full` }>
          <DataGridPro
            rows={ rows }
            columns={ columns }
            loading={ isLoading }
            rowHeight={ 38 }
            initialState={ {
              sorting: {
                sortModel: [{
                  field: `issueDateString`,
                  sort: `desc`,
                }],
              },
            } }
            localeText={ {
              noRowsLabel: `No ${quotesTerm.toLowerCase()} found for this contact`,
            } }
            sx={ { ...commonConfig.headerStyle } }
          />
        </DatagridCard>
      </div>
    </div>
  );
}
